import { rgba } from 'polished';
import { index, Options } from 'shared/assets/styles';
import { typographyToCss } from 'shared/lib/mui';
import { isNumber } from 'shared/utils/is-number';
import styled, { css, keyframes } from 'styled-components';

import { ButtonVariants, Colors } from './types';

export const positioningOffset = 24;

const inAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const Backdrop = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: ${index(Options.TOOLTIP)};
`;

export const Container = styled.div<{
  $isMac: boolean;
  $width: number | undefined;
}>`
  display: flex;
  flex-direction: column;

  min-width: 21.6rem;
  width: fit-content;
  max-width: calc(100% - (${positioningOffset}px * 2));
  max-height: ${({ $isMac }) =>
    `calc(100vh - (${$isMac ? positioningOffset * 2 : positioningOffset}px * 2))`};

  z-index: ${index(Options.TOOLTIP)};
  border-radius: ${({ theme }) => theme.spacing(4)};
  border: 1px solid ${({ theme }) => theme.palette.grey[400]};
  background: ${({ theme }) => rgba(theme.palette.grey[100], 0.85)};
  backdrop-filter: blur(24px);
  box-shadow: ${({ theme }) => theme.shadowBox.menu};
  animation: ${inAnimation} 200ms linear;

  ${({ $width }) =>
    isNumber($width) &&
    css`
      min-width: unset;
      width: ${$width}px;
    `}
`;

/**
 * A dedicated scroll container to ensure reliable scrolling behavior in Safari.
 * This prevents the menu background from shifting or scrolling away when
 * reaching the boundaries of the content.
 */
export const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
`;

const listBase = css<{ $hasBottomBorder: boolean; $isScrollable: boolean }>`
  ${({ theme, $hasBottomBorder }) =>
    $hasBottomBorder &&
    css`
      border-bottom: 1px solid ${theme.palette.grey[400]};
    `};

  ${({ $isScrollable }) =>
    $isScrollable &&
    css`
      min-height: 0;
      height: inherit;
      overflow: auto;
    `};

  &:last-child {
    border-bottom: none;
  }
`;

export const ListGroup = styled.div<{
  $hasBottomBorder: boolean;
  $isScrollable: boolean;
}>`
  ${listBase};
`;

export const List = styled.ul<{
  $hasBottomBorder: boolean;
  $isScrollable: boolean;
}>`
  ${listBase};
  margin: 0;
  padding: ${({ theme }) => theme.spacing(1)};

  list-style: none;
`;

export const ListItem = styled.li`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const itemBase = css<{ $color?: Colors; $level?: number; $compact: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.body2)};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};

  padding: ${({ theme, $level, $compact }) =>
    `${theme.spacing($compact ? 1 : 1.5)} ${theme.spacing(3)}
    ${theme.spacing($compact ? 1 : 1.5)} ${theme.spacing(3 + ($level ?? 0) * 2)}`};
  margin: 0;
  width: 100%;
  min-width: 0;
  max-width: 100%;

  background: transparent;
  border: none;
  border-radius: ${({ theme }) => theme.spacing(2.5)};
  transition: background 200ms;

  text-align: left;
  color: ${({ theme, $color }) => {
    switch ($color) {
      case Colors.Primary:
        return theme.palette.primary.main;
      case Colors.Warning:
        return theme.palette.warning.main;
      case Colors.Error:
        return theme.palette.error.dark;
      case Colors.Info:
        return theme.palette.info.main;
      case Colors.TextSecondary:
        return theme.palette.text.secondary;
      case Colors.Dark:
        return theme.palette.text.primary;
      case Colors.Grey600:
        return theme.palette.grey[600];
      default:
        return theme.palette.text.primary;
    }
  }};
`;

const interActiveItemBase = css<{
  $compact: boolean;
  $color?: Colors;
  $noHover?: boolean;
  $level?: number;
}>`
  ${itemBase};
  cursor: pointer;

  -webkit-app-region: no-drag;

  ${({ $noHover }) =>
    !$noHover &&
    css`
      &:hover,
      &:focus,
      &:focus-visible {
        background: ${({ theme }) => rgba(theme.palette.text.primary, 0.06)};
        outline: none;
      }
    `};
`;

export const Button = styled.button<{
  $color?: Colors;
  $isSelected: boolean;
  $indentationLevel: number;
  $compact: boolean;
  $variant: ButtonVariants | undefined;
}>`
  ${interActiveItemBase};

  ${({ theme, $indentationLevel }) =>
    $indentationLevel &&
    css`
      padding-left: ${`calc(${theme.spacing(3)} + ${theme.spacing(6)} * ${$indentationLevel})`};
    `}

  ${({ $isSelected }) =>
    $isSelected &&
    css`
      ${({ theme }) => typographyToCss(theme.typography.subtitle2)};
      background: ${({ theme }) => rgba(theme.palette.text.primary, 0.06)};
    `};

  ${({ theme, $variant }) =>
    $variant === ButtonVariants.Error &&
    css`
      ${typographyToCss(theme.typography.buttonSmall)};
      background: ${theme.palette.background.default};
      transition: background 150ms;

      color: ${theme.palette.error.dark};
      text-align: center;

      &:hover {
        background: rgba(${theme.palette.text.primary}, 0.06);
      }
    `}

  ${({ theme, $variant }) =>
    $variant === ButtonVariants.Outlined &&
    css`
      ${typographyToCss(theme.typography.buttonSmall)};
      background: ${theme.palette.background.default};
      transition: background 150ms;

      color: ${theme.palette.text.primary};
      text-align: center;

      &:hover {
        background: rgba(${theme.palette.text.primary}, 0.06);
      }
      &:disabled {
        cursor: default;
        opacity: 0.6;
        &:hover {
          background: ${theme.palette.background.default};
        }
      }
    `}
`;

export const InputLabel = styled.label<{
  $color?: Colors;
  $noHover?: boolean;
  $compact: boolean;
}>`
  ${interActiveItemBase};
  position: relative;
`;

export const Toggle = styled.label<{
  $color?: Colors;
  $noHover?: boolean;
  $compact: boolean;
}>`
  ${interActiveItemBase};
`;

export const ToggleContent = styled.span`
  flex: 1;

  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  justify-content: space-between;
  align-items: center;
`;

export const CheckboxContainer = styled.label<{
  $color?: Colors;
  $level?: number;
  $compact: boolean;
}>`
  ${interActiveItemBase};
`;

export const CheckboxLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Select = styled.label<{
  $isFilterStyle?: boolean;
}>`
  ${({ $isFilterStyle }) =>
    $isFilterStyle &&
    css`
      width: 59%;
    `}
`;

export const SelectContainer = styled.label<{
  $compact: boolean;
  $color?: Colors;
  $level?: number;
  $noHover?: boolean;
}>`
  ${interActiveItemBase};
`;

export const SelectContent = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.body2)};
  flex: 1;
  min-width: 0;

  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  justify-content: space-between;
  align-items: center;
`;

export const SelectLabel = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

export const ListItemGroup = styled.label<{
  $color?: Colors;
  $noHover?: boolean;
  $compact: boolean;
}>`
  ${interActiveItemBase};
`;

export const ListItemGroupContent = styled.span`
  flex: 1;

  display: flex;
  gap: ${({ theme }) => theme.spacing(2)};
  align-items: center;
`;

export const TitleContainer = styled.span<{
  $color?: Colors;
  $hasBottomBorder: boolean;
  $compact: boolean;
  $isSubTitle?: boolean;
}>`
  justify-content: space-between;
  display: flex;
  padding: ${({ theme, $isSubTitle }) =>
    $isSubTitle
      ? `${theme.spacing(2)} ${theme.spacing(3)} ${theme.spacing(1)} ${theme.spacing(3)}`
      : theme.spacing(4)};

  ${({ theme, $hasBottomBorder }) =>
    $hasBottomBorder &&
    css`
      border-bottom: 1px solid ${theme.palette.grey[400]};
    `};
`;

export const Title = styled.span<{ $isSubTitle?: boolean }>`
  ${({ theme, $isSubTitle }) =>
    typographyToCss(
      $isSubTitle ? theme.typography.caption : theme.typography.h6,
    )};
  color: ${({ theme, $isSubTitle }) =>
    $isSubTitle ? theme.palette.text.secondary : theme.palette.text.primary};
`;

export const ButtonContent = styled.span<{ $overflowEllipsis: boolean }>`
  flex: 1;
  min-width: 0;

  ${({ $overflowEllipsis }) =>
    $overflowEllipsis &&
    css`
      overflow: hidden;

      text-overflow: ellipsis;
      white-space: nowrap;
    `};
`;

export const Input = styled.input<{ $hidden: boolean; $hasStartIcon: boolean }>`
  ${({ theme }) => typographyToCss(theme.typography.body2)};
  position: absolute;
  top: 0;
  right: ${({ theme }) => theme.spacing(3)};
  bottom: 0;
  left: ${({ $hasStartIcon, theme }) => ($hasStartIcon ? theme.spacing(9) : 0)};

  border: none;
  background: transparent;

  color: inherit;
  outline: none;

  -webkit-app-region: no-drag;

  ${({ $hidden }) =>
    $hidden &&
    css`
      top: unset;
      right: unset;
      bottom: unset;
      left: unset;
      width: 0;
      height: 0;
      margin: 0;
      padding: 0;

      border: none;
      visibility: hidden;
    `};
`;

export const AffixContainer = styled.span<{
  $color?: Colors;
  $isSelected?: boolean;
}>`
  font-size: 1.6rem;
  line-height: 62.5%;

  color: ${({ theme, $color }) => {
    switch ($color) {
      case Colors.Primary:
        return theme.palette.primary.main;
      case Colors.Warning:
        return theme.palette.warning.main;
      case Colors.Error:
        return theme.palette.error.dark;
      case Colors.Info:
        return theme.palette.info.main;
      case Colors.TextSecondary:
        return theme.palette.text.secondary;
      case Colors.Dark:
        return theme.palette.text.primary;
      case Colors.Grey600:
        return theme.palette.grey[600];
      default:
        return theme.palette.text.primary;
    }
  }};

  ${({ $isSelected, theme }) =>
    $isSelected &&
    css`
      color: ${theme.palette.text.primary};
    `};
`;

export const IconContainer = styled.span<{ $color?: Colors }>`
  font-size: 1.6rem;
`;

export const PremiumLabel = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.overline)};
  display: block;

  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};

  background: ${({ theme }) => theme.palette.info.light};
  border-radius: ${({ theme }) => theme.spacing(6)};

  color: ${({ theme }) => theme.palette.info.main};
`;

export const RightSideItems = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
`;
